import React, { useState, useEffect, useCallback } from 'react'

//components
import Card from '@/components/Spellify/Card/Card'
import ControlPanel from '@/components/Spellify/ControlPanel/ControlPanel'
import Modal from '@/components/UI/Modal/Modal'
import Button from '@/components/UI/Button/Button'
import EndMessages from '../EndMessages/EndMessages'
import GuessCardView from '@/components/Spellify/GuessCardView/GuessCardView'
import Countdown from '../EndMessages/Countdown/Countdown'

//util functions and constants
import { numbers } from '@/lib/constants'
import { ScryfallCard } from "@scryfall/api-types";

//styles
import styles from './GameFields.module.scss'
import {convertNameToGuessArray} from "@/lib/util";
import fetch from "node-fetch";
import HistoryView from '../HistoryView/HistoryView'
import Icon from "@/components/UI/Icon/Icon";
import {useIsAdmin} from "@/lib/useIsAdmin";
import classNames from 'classnames'
import {useRouter} from "next/router";
import {SponsorshipType} from "@/types/spellify/sponshorship";
import SponsorshipOverlay from "@/components/Spellify/SponsorshipOverlay/SponsorshipOverlay";
import {useSponsorshipBackground} from "@/utils/sponsorships";
import Dimmer from "@/components/UI/Dimmer/Dimmer";
import {useSpellifyState} from "@/pages/spellify";
import useSpellifyMode, {DEFAULT_GAME_STATS, MODE_PATHS} from "@/lib/useSpellifyMode";
import StartOverlay from "@/components/StartOverlay/StartOverlay";
import spellifyIcon from "@/assets/images/spellifyIcon.png"
import saltAdDesktop from "@/assets/ads/salt_desktop.png"
import saltAdMobile from "@/assets/ads/salt_mobile.png"

type Props = {
  dailyCard: ScryfallCard.Normal
  hardDailyCard: ScryfallCard.Normal
  spellifyNumber: number
  hardSpellifyNumber: number
  sponsorship: SponsorshipType | null
  saltCards?: ScryfallCard.Normal[] | null
  saltNumber?: number
}

const GameFields = ({dailyCard, spellifyNumber, hardDailyCard, hardSpellifyNumber, sponsorship, saltCards, saltNumber}: Props) => {

  const sponsorshipBackground = useSponsorshipBackground(sponsorship)

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const router = useRouter()
  const isAdmin = useIsAdmin()

  // Everything that differs between normal, hard and salt mode lives here
  const {
    mode, label, isHardMode, isSaltMode, isInfiniteMode,
    card, puzzleNumber, isNewDay,
    buttonState, setButtonState,
    guesses, setGuesses,
    cardGuesses, setCardGuesses,
    gameState, setGameState,
    gameStats, setGameStats,
    playedIncremented, setPlayedIncremented,
    saltIndex, saltCardCount, hasNextSaltCard, newDayCard, goToNextSaltCard,
    resetForNewDay, replaceDailyCard,
  } = useSpellifyMode({dailyCard, hardDailyCard, spellifyNumber, hardSpellifyNumber, saltCards, saltNumber})

  const [isGuessing, setIsGuessing] = useState(false)
  const [isViewingHistory, setIsViewingHistory] = useState(false)
  const [guessIndex, setGuessIndex] = useState(0)
  const [currentGuess, setCurrentGuess] = useState<string[]>(convertNameToGuessArray(card.name))

  const [hasReadRules, setHasReadRules] = useSpellifyState('spellifyHasReadRules', false)
  const [rulesOpen, setRulesOpen] = useState(false)

  const [notifyingWrongGuess, setNotifyingWrongGuess] = useState(false)

  const [loading, setLoading] = useState(false)

  // The number stamped on stats and the start overlay. Hard mode has always shown the normal mode
  // number in both places, only its share message uses hardSpellifyNumber.
  const dailyNumber = isSaltMode ? puzzleNumber : spellifyNumber

  const winSuffix = mode === 'hard' ? ' hard mode' : mode === 'salt' ? ` card ${saltIndex + 1} of ${saltCardCount}` : ''

  // Partway through the salt set the end modal is all about moving on, the timer and mode switch wait for the last card
  const isMidSaltSet = isSaltMode && hasNextSaltCard

  useEffect(() => {
    if (isNewDay) restart()
    else if(gameState !== 'guessing') setModalOpen(true)
  }, [])

  const guessLimit = 13

  const oracleTextParts = card.oracle_text.split('\n') || ''
  const typeParts = card.type_line.split('—') || ''
  let validLetters = Array.from(
    new Set([
              card.name.toUpperCase(),
              typeParts.length === 2 ? typeParts[1]!.toUpperCase() : "",
              (card.power || '').toUpperCase(),
              (card.toughness || '').toUpperCase(),
            ]
            .join(',').split(',').join(','))
  )

  // use regex to split oracle text into every character unless between {}, then keep whole string between {}
  const oracleSymbols = Array.from(card.oracle_text.match(/{(.*?)}/g) || [])
  const oracleLetters = oracleSymbols.reduce((acc, symbol) => {
    return acc.replace(symbol, '')
  }, card.oracle_text.toLowerCase().replace(' ', '')).split('')


  const costSplit = Array.from((card.mana_cost || '').match(/{(.*?)}/g) ||[])

  validLetters = [...validLetters, ...oracleSymbols, ...oracleLetters, ...costSplit]
  validLetters = Array.from(new Set(validLetters))

  // build an array of empty strings equal to the guess limit
  const guessList: Array<'' | 'correct' | 'incorrect'> = Array.from({length: guessLimit}, () => "")
  guesses?.forEach((guess, i) => {
    if (i % 2 === 0) {
      if (validLetters.includes(guess) || validLetters.includes(guess.toLowerCase())) guessList[i / 2] = 'correct'
      else guessList[i / 2] = 'incorrect'
    }
  })

  const isLastGuess = (guesses?.length || 0) / 2 >= guessLimit - 1

  const handleGuess = useCallback((s: string) => {
    if (isLastGuess) {
      setIsGuessing(true)
      return
    }
    if (isViewingHistory) return
    if (s.length > 1 && s[0] !== '{') return
    if ((guesses || []).length === 0) {
      const currentStats = gameStats || DEFAULT_GAME_STATS
      setGameStats({...currentStats, gamesPlayed: currentStats.gamesPlayed + 1})
      setPlayedIncremented(true)
    }
    if (!((guesses || []).includes(s) || (guesses || []).includes(s.toLowerCase()) ) && ((guesses || []).length / 2 < guessLimit) && gameState === 'guessing') {
      if (numbers.includes(s)) {
        setGuesses([...(guesses || [] ), ...[s, `{${s}}`]])
      } else {
        setGuesses([...(guesses || [] ), ...[s, s.toLowerCase()]]) // All the code should now be case-insensitive, but we still store both cases because legacy code expects two entries per guess, X being the exception since it's caught by the clause above
      }
      if (validLetters.includes(s) || validLetters.includes(s.toLowerCase())) {
        setButtonState({
          ...buttonState,
          [s.toUpperCase()]: 'green'
        })
      } else {
        setButtonState({
          ...buttonState,
          [s.toUpperCase()]: 'red'
        })
      }
    }

    if ((guesses || []).length / 2 >= guessLimit -1) {
      const currentStats = gameStats || DEFAULT_GAME_STATS
      setGameState('failure')
      setModalOpen(true)
      setGameStats({
        ...currentStats,
        currentStreak: 0,
        recentGuessNumber: -1,
        winRate: (currentStats.gamesWon) / currentStats.gamesPlayed * 100,
      })
    }
  }, [guesses, buttonState, validLetters, gameStats])

  const handleGuessModeKeyPress = (key: string) => {
    if (key.length === 1 && key.toLowerCase().match(/^[0-9a-z]+$/)) {
      const newGuess = [...currentGuess]
      newGuess[guessIndex] = key
      setCurrentGuess(newGuess)
      if (guessIndex < currentGuess.length - 1) {
        setGuessIndex(guessIndex + 1)
      }
    }
    if (key === 'Backspace' && guessIndex > 0) {
      const newGuess = [...currentGuess]
      if (guessIndex === currentGuess.length - 1 && newGuess[guessIndex]) {
        newGuess[guessIndex] = ''
      } else {
        newGuess[guessIndex-1] = ''
        setGuessIndex(guessIndex - 1)
      }
      setCurrentGuess(newGuess)
    }
    if (key === 'Delete') {
      const newGuess = [...currentGuess]
      newGuess[guessIndex] = ''
      setCurrentGuess(newGuess)
    }
    if (key === 'ArrowLeft' && guessIndex > 0)  setGuessIndex(guessIndex - 1)
    if (key === 'ArrowRight' && guessIndex < currentGuess.length - 1)  setGuessIndex(guessIndex + 1)
    if (key === 'Enter') handleGuessCard()
  }

  // Clears the board itself, the stored progress behind it belongs to useSpellifyMode
  function resetBoard(nextCard: ScryfallCard.Normal) {
    setCurrentGuess(convertNameToGuessArray(nextCard?.name || ''))
    setGuessIndex(0)
    setIsGuessing(false)
    setModalOpen(false)
  }

  // A new puzzle: throw away yesterday's progress for whichever mode is being played
  function restart() {
    resetForNewDay()
    resetBoard(newDayCard)
  }

  const handleNextSaltCard = () => {
    goToNextSaltCard()
    setModalOpen(false)
    setIsGuessing(false)
  }

  const getNewCard = async () => {
    setLoading(true)
    setPlayedIncremented(false)
    let nextUrl = "https://api.scryfall.com/cards/search?q=order%3Aedhrec+-is%3Adoublesided+-is%3Aadventure+-is%3Asplit+-t%3Asaga+-t%3Aclass+-type%3Aplaneswalker+-o%3A%2F%7B%5BWUBRG1-9%5D%5C%2F%5BWUBRG1-9%5D%7D%2F+-is%3Ahybrid"
    if (!isHardMode) nextUrl += "+-type%3Aland" // Exclude lands from easy mode

    let page = 1
    if (isHardMode) page = Math.floor(Math.random() * (29 - 12 + 1) + 12)
    else page = Math.floor(Math.random() * 12)
    nextUrl += `&page=${page}`

    nextUrl += "&unique=cards"

    const cards = (await fetch(nextUrl).then(res => res.json())).data
    const randomCard = cards[Math.floor(Math.random() * cards.length)]
    replaceDailyCard(randomCard)
    resetBoard(randomCard)
    setLoading(false)
  }

  const handleGuessCard = () => {
    if (card.name.toLowerCase().replace(/[^0-9a-z]/gi, '') === currentGuess?.join('').toLowerCase() && gameState !== 'failure') {
      if (gameState === 'guessing') {
        const currentStats = gameStats || DEFAULT_GAME_STATS

        const calcGamesPlayed = playedIncremented ? currentStats.gamesPlayed : currentStats.gamesPlayed + 1

        //sanity check for anyone who's count has gotten thrown off
        //if gamesPlayed is less than the sum of guessHistory, update gamesPlayed to the sum of guessHistory
        const sumGuessHistory = Object.values(currentStats.guessHistory).reduce((a, b) => a+b, 0) + 1
        //guessHistory only accounts for games that you've won, so it can be less than gamesPlayed
        //if they are equal, default to gamesPlayed
        const finalGamesPlayed = sumGuessHistory > calcGamesPlayed ? sumGuessHistory : calcGamesPlayed

        setGameStats({
          ...currentStats,
          spellifyNumber: dailyNumber,
          gamesPlayed: finalGamesPlayed,
          gamesWon: currentStats.gamesWon + 1,
          currentStreak: currentStats.currentStreak + 1,
          maxStreak: currentStats.currentStreak + 1 > currentStats.maxStreak ? currentStats.currentStreak + 1 : currentStats.maxStreak,
          winRate: (currentStats.gamesWon + 1) / (currentStats.gamesPlayed > 0 ? finalGamesPlayed : 1) * 100,
          recentGuessNumber: (guesses || []).length / 2,
          guessList: guessList,
          guessHistory: {
            ...currentStats.guessHistory,
            [`${(guesses || []).length/2}`]: currentStats.guessHistory[(guesses || []).length / 2] + 1
          }
        })

        setPlayedIncremented(true)
      }

      setGameState('success')
      setIsGuessing(false)
      setModalOpen(true)
    } else if ((guesses || []).length / 2 < guessLimit -1) {
      // wrong guess, but not failed
      setGuesses([...(guesses || []), ...[`${currentGuess?.join('').toLowerCase()}`, `${currentGuess?.join('').toLowerCase()}`]])
      setCardGuesses([...(cardGuesses || []), ...[currentGuess]])
      setNotifyingWrongGuess(true)
      setTimeout(() => {
        setNotifyingWrongGuess(false)
        setCurrentGuess(convertNameToGuessArray(card.name))
        setGuessIndex(0)
      }, 500)
    } else if ((guesses || []).length / 2 >= guessLimit -1) {
      // fail state
      setNotifyingWrongGuess(true)
      setTimeout(() => {
        setNotifyingWrongGuess(false)
      }, 2000)
      if (gameState === 'guessing') {
        const currentStats = gameStats || DEFAULT_GAME_STATS
        setGameStats({
          ...currentStats,
          spellifyNumber: dailyNumber,
          currentStreak: 0,
          recentGuessNumber: -1,
          guessList: guessList,
          winRate: (currentStats.gamesWon) / currentStats.gamesPlayed * 100,
        })
      }

      setGameState('failure')
      setIsGuessing(false)
      setModalOpen(true)
    }
  }

  const handleModeChange = () => {
    router.push(mode === 'normal' ? MODE_PATHS.hard : MODE_PATHS.normal)
    setGuessIndex(0)
    setIsGuessing(false)
  }

  useEffect(() => {
    if (!hasReadRules) setTimeout(() => setRulesOpen(true), 500)
  }, [hasReadRules]);

  useEffect(() => {
    if (isLastGuess && gameState === 'guessing') setIsGuessing(true)
  }, [isLastGuess]);

  useEffect(() => {
    // scroll to bottom of page on load in case there any scrolling (happens on older iphones)
    window.scrollTo(0,document.body.scrollHeight);
  }, [])

  // Any time the card being played changes - switching modes, the next salt card, a new day - the
  // guess field has to be resized for the new name
  useEffect(() => {
    setCurrentGuess(convertNameToGuessArray(card.name))
    setGuessIndex(0)
  }, [card.name]);

  if (!card) return null

  return (
    <div className={styles.gamefield} style={sponsorshipBackground}>
      {loading && <Dimmer loading={loading}/>}
      <StartOverlay
        key={`start-overlay-${mode}`}
        title={`Spellify ${label}`.trim()}
        description={isSaltMode
          ? `Five of the saltiest cards in Commander. Guess letters until you can name each one`
          : "Guess letters until you can name the Magic card"}
        dateString={ new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) }
        number={dailyNumber}
        logo={spellifyIcon.src}
      >
        <a href="https://edhrec.com/salt" target="_blank" rel="noreferrer" className={styles.saltAd}>
          <img src={saltAdDesktop.src} alt="See the saltiest cards in Commander on EDHREC" className={styles.saltAdDesktop}/>
          <img src={saltAdMobile.src} alt="See the saltiest cards in Commander on EDHREC" className={styles.saltAdMobile}/>
        </a>
      </StartOverlay>
      <SponsorshipOverlay sponsorship={sponsorship}/>
      <Icon icon="infoCircle" className={styles.rulesIcon} onClick={() => setRulesOpen(true)}/>
      <Modal
        closeIcon
        open={rulesOpen}
        onClose={() => {setRulesOpen(false); setHasReadRules(true)}}
        header="Spellify Rules"
        >
        <div>
          <h5>
            Welcome to Spellify, a fun guessing game based on Magic: The Gathering cards!

            Here's how to play:
          </h5>
          <ul>
            <li>You'll try to figure out the name of a card by guessing letters, numbers, and symbols.</li>
            <li>If you guess right, we'll show you more of the card's name and text.</li>
            <li>You can try to guess the whole card name anytime by pressing “Guess card…”.</li>
            <li>Be careful: you only get 13 tries to guess, and that includes trying to guess the card name!</li>
          </ul>
        </div>
      </Modal>
      <Modal
        closeIcon
        open={gameState !== 'guessing' && modalOpen}
        onClose={() => {setModalOpen(false)}}
        footer={
          isSaltMode
            ? (hasNextSaltCard ? <Button onClick={handleNextSaltCard}>Next Card ({saltIndex + 2} of {saltCardCount})</Button> : undefined)
            : (isAdmin || isInfiniteMode) ? <Button onClick={() => {setModalOpen(false); getNewCard()}}>Next Card</Button> : undefined
        }
        header={
          gameState === 'success' ?
          <span className={styles.header}>You won{winSuffix} in<div className={classNames(!guesses?.length && styles.zero)}>{(guesses || []).length/2}</div> guesses!</span>
          : "Sorry, that's all the guesses you get"}
        contentClassName={styles.finalModal}
      >
        {isMidSaltSet && (
          <button type="button" className={styles.cta} onClick={handleNextSaltCard}>
            <Icon icon="circleExclamation" className={styles.ctaIcon}/>
            <span className={styles.ctaText}>
              <strong>There's more salt in the shaker!</strong>
              <span>Up next: card {saltIndex + 2} of {saltCardCount}</span>
            </span>
            <Icon icon="chevronRight"/>
          </button>
        )}
        {!isSaltMode && (
          <button type="button" className={styles.cta} onClick={() => router.push(MODE_PATHS.salt)}>
            <Icon icon="circleExclamation" className={styles.ctaIcon}/>
            <span className={styles.ctaText}>
              <strong>It's Salt Week!</strong>
              <span>Try guessing five different salty cards each day!</span>
            </span>
            <Icon icon="chevronRight"/>
          </button>
        )}
        {isSaltMode && !hasNextSaltCard && <h3 style={{textAlign: 'center'}}>That's all five of today's saltiest cards!</h3>}
        {!isMidSaltSet && <Countdown />}
        {!isMidSaltSet && (
          <div className={styles.buttonContainer}>
            {mode !== 'normal' && <Button onClick={handleModeChange}><Icon icon="check" /> Return to Normal Mode</Button>}
            {mode === 'normal' && <Button onClick={handleModeChange} title="Any card, not just EDH staples..."><Icon icon="trophy"/> Try Hard Mode</Button>}
          </div>
        )}

        {!isSaltMode && `${spellifyNumber}` === process.env.NEXT_PUBLIC_PREVIEW_CARD_SPELLIFY_NUMBER && <h3 style={{textAlign: 'center'}}>{process.env.NEXT_PUBLIC_PREVIEW_MESSAGE}</h3>}
        <div className={styles.imageContainer}>
          <img src={card.image_uris?.large} alt={card.name} width={250}/>
        </div>
        <EndMessages
          mode={mode}
          saltCardNumber={saltIndex + 1}
          saltCardCount={saltCardCount}
          spellifyNumber={puzzleNumber}
          card={card}
          gameState={gameState || "guessing"}
          guessCount={(guesses || []).length / 2}
          stats={gameStats || DEFAULT_GAME_STATS}
          restartFunc={getNewCard}
        ></EndMessages>
      </Modal>
      <div className={styles.topContainer}>
        <Card
          card={card}
          oracleTextParts={oracleTextParts}
          typeParts={typeParts}
          correctGuesses={guesses || []}
          isHardMode={isHardMode}
        />
        <GuessCardView
          setGuessIndex={setGuessIndex}
          cardGuesses={cardGuesses || []}
          isGuessing={isGuessing}
          card={card}
          guesses={guesses}
          guessIndex={guessIndex}
          currentGuess={currentGuess}
          onGuess={handleGuessCard}
          notifyingWrongGuess={notifyingWrongGuess}
        />
        <HistoryView isViewingHistory={isViewingHistory} guesses={guesses || []} card={card} buttonState={buttonState || {}}/>
      </div>

      <ControlPanel
        setModalOpen={setModalOpen}
        guessLimit={guessLimit}
        guessFunc={isGuessing ? handleGuessModeKeyPress : handleGuess}
        buttonState={buttonState}
        guessList={guessList}
        setIsGuessing={setIsGuessing}
        setIsViewingHistory={setIsViewingHistory}
        isViewingHistory={isViewingHistory}
        isGuessing={isGuessing}
        gameState={gameState || 'guessing'}
      />
    </div>
  )
}

export default GameFields